import React, { useEffect, useState } from 'react';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Button,
  Avatar,
  Card,
  CardContent,
  CircularProgress,
  Fade,
  Box,
  Chip,
  Zoom
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import StarIcon from '@mui/icons-material/Star';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import BoltIcon from '@mui/icons-material/Bolt';
import PublicIcon from '@mui/icons-material/Public';
import SecurityIcon from '@mui/icons-material/Security';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/+$/, '');

const PROVIDERS = [
  {
    key: 'payos',
    value: 'payos',
    icon: <AccountBalanceWalletOutlinedIcon />,
    color: '#00B14F',
    gradient: 'linear-gradient(135deg, #00B14F 0%, #00d465 100%)',
    labelKey: 'payment.provider.payos.label',
    captionKey: 'payment.provider.payos.caption',
    defaultLabel: 'PayOS',
    defaultCaption: 'QR Pay, Bank Transfer, MoMo, ZaloPay',
    badge: 'recommended',
    badgeIcon: <BoltIcon sx={{ fontSize: 12 }} />,
    features: ['instant', 'local', 'auto']
  },
  {
    key: 'wallet',
    value: 'momo',
    icon: <AccountBalanceWalletOutlinedIcon />,
    color: '#A50064',
    gradient: 'linear-gradient(135deg, #A50064 0%, #ff6b9d 100%)',
    labelKey: 'payment.provider.momo.label',
    captionKey: 'payment.provider.momo.caption',
    defaultLabel: 'MoMo',
    defaultCaption: 'Fast and easy for students',
    badge: 'popular',
    badgeIcon: <StarIcon sx={{ fontSize: 12 }} />,
    features: ['instant', 'local']
  },
  {
    key: 'paypal',
    value: 'paypal',
    icon: <PaidOutlinedIcon />,
    color: '#003087',
    gradient: 'linear-gradient(135deg, #003087 0%, #009cde 100%)',
    labelKey: 'payment.provider.paypal.label',
    captionKey: 'payment.provider.paypal.caption',
    defaultLabel: 'PayPal',
    defaultCaption: 'International payment via PayPal',
    badge: 'global',
    badgeIcon: <PublicIcon sx={{ fontSize: 12 }} />,
    features: ['international', 'secure']
  },
  {
    key: 'card',
    value: 'card',
    icon: <CreditCardOutlinedIcon />,
    color: '#1976d2',
    gradient: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
    labelKey: 'payment.provider.card.label',
    captionKey: 'payment.provider.card.caption',
    defaultLabel: 'Visa/MasterCard',
    defaultCaption: 'Pay with credit or debit card',
    badge: 'secure',
    badgeIcon: <SecurityIcon sx={{ fontSize: 12 }} />,
    features: ['cards', 'secure']
  }
];

export default function PaymentMethodPickerDialog({ open, onClose, onSelect, planType, subtitle, alertMessage }) {
  const { t } = useTranslation();
  const [flags, setFlags] = useState({ card: false, wallet: false, paypal: false, payos: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [hoveredKey, setHoveredKey] = useState(null);

  const planLabel = planType === 'PAID_YEARLY'
    ? t('subscription.yearly', 'Yearly')
    : t('subscription.monthly', 'Monthly');

  const subText = subtitle || t('payment.chooseMethodDesc', 'Select your preferred payment method for the {{period}} plan', { period: planLabel });

  useEffect(() => {
    let mounted = true;
    if (open) {
      setLoading(true);
      setError(false);
      (async () => {
        try {
          const res = await fetch(`${API_BASE}/public/settings/payment-methods`);
          if (!mounted) return;
          if (res.ok) {
            const data = await res.json();
            setFlags({
              card: data.card === true,
              wallet: data.wallet === true,
              paypal: data.paypal === true,
              payos: data.payos === true,
            });
          } else {
            setError(true);
          }
        } catch {
          if (mounted) setError(true);
        } finally {
          if (mounted) setLoading(false);
        }
      })();
    }
    return () => { mounted = false; };
  }, [open]);

  const availableProviders = PROVIDERS.filter(p => flags[p.key]);

  const getBadgeLabel = (badge) => {
    switch (badge) {
      case 'recommended': return t('payment.badge.recommended', 'Recommended');
      case 'popular': return t('payment.badge.popular', 'Popular');
      case 'global': return t('payment.badge.global', 'Global');
      case 'secure': return t('payment.badge.secure', 'Secure');
      case 'auto': return t('payment.badge.auto', 'Auto');
      default: return '';
    }
  };

  const getBadgeColor = (badge) => {
    switch (badge) {
      case 'recommended': return 'success';
      case 'popular': return 'warning';
      case 'global': return 'info';
      case 'secure': return 'success';
      case 'auto': return 'success';
      default: return 'default';
    }
  };

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="choose-payment-method-title"
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 400 }}
      PaperProps={{
        elevation: 8,
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          background: (theme) => theme.palette.mode === 'dark'
            ? 'linear-gradient(180deg, rgba(30,30,40,0.98) 0%, rgba(20,20,30,0.98) 100%)'
            : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)'
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        id="choose-payment-method-title"
        sx={{
          pb: 1,
          background: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)'
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Zoom in timeout={300}>
            <Avatar
              sx={{
                width: 52,
                height: 52,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 14px rgba(102,126,234,0.4)'
              }}
            >
              <PaymentsOutlinedIcon />
            </Avatar>
          </Zoom>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.5 }}>
              {t('payment.chooseMethodTitle', 'Choose Payment Method')}
            </Typography>
            <Chip
              size="small"
              label={planLabel}
              sx={{
                mt: 0.5,
                fontWeight: 600,
                bgcolor: 'primary.main',
                color: 'white'
              }}
            />
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {alertMessage && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            {alertMessage}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {subText}
        </Typography>

        <Box minHeight={140}>
          {loading ? (
            <Fade in={loading} unmountOnExit>
              <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                <CircularProgress size={40} sx={{ color: 'primary.main' }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {t('payments.picker.loading', 'Loading payment methods...')}
                </Typography>
              </Stack>
            </Fade>
          ) : error ? (
            <Fade in>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="error">
                  {t('payments.picker.error', 'Failed to load payment methods. Please try again.')}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 2, borderRadius: 2 }}
                  onClick={() => { setLoading(true); setError(false); }}
                >
                  {t('common.retry', 'Retry')}
                </Button>
              </Box>
            </Fade>
          ) : availableProviders.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
              {t('payments.picker.noMethods', 'No payment methods available. Please contact support.')}
            </Typography>
          ) : (
            <Stack spacing={2}>
              {availableProviders.map((provider, index) => (
                <Zoom in timeout={300 + index * 100} key={provider.key}>
                  <Card
                    elevation={hoveredKey === provider.key ? 8 : 2}
                    onMouseEnter={() => setHoveredKey(provider.key)}
                    onMouseLeave={() => setHoveredKey(null)}
                    onClick={() => { onSelect && onSelect(provider.value); }}
                    sx={{
                      position: 'relative',
                      cursor: 'pointer',
                      borderRadius: 3,
                      overflow: 'hidden',
                      border: '2px solid',
                      borderColor: hoveredKey === provider.key ? provider.color : 'transparent',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      transform: hoveredKey === provider.key ? 'translateY(-4px) scale(1.02)' : 'none',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: hoveredKey === provider.key ? 6 : 4,
                        background: provider.gradient,
                        transition: 'width 0.3s ease'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 2.5, pl: 3.5 }}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar
                          sx={{
                            width: 48,
                            height: 48,
                            background: provider.gradient,
                            boxShadow: `0 4px 12px ${alpha(provider.color, 0.3)}`,
                            transition: 'transform 0.3s ease',
                            transform: hoveredKey === provider.key ? 'scale(1.1)' : 'scale(1)'
                          }}
                        >
                          {provider.icon}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {t(provider.labelKey, provider.defaultLabel)}
                            </Typography>
                            {provider.badge && (
                              <Chip
                                size="small"
                                icon={provider.badgeIcon}
                                label={getBadgeLabel(provider.badge)}
                                color={getBadgeColor(provider.badge)}
                                sx={{
                                  height: 22,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  '& .MuiChip-icon': { fontSize: 12 }
                                }}
                              />
                            )}
                          </Stack>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            {t(provider.captionKey, provider.defaultCaption)}
                          </Typography>
                        </Box>
                        <ChevronRightIcon
                          sx={{
                            color: hoveredKey === provider.key ? provider.color : 'text.disabled',
                            transition: 'all 0.3s ease',
                            transform: hoveredKey === provider.key ? 'translateX(4px)' : 'none'
                          }}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                </Zoom>
              ))}
            </Stack>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 2.5,
            px: 3,
            fontWeight: 600
          }}
        >
          {t('common.cancel', 'Cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
